import { inject, Injectable, signal } from '@angular/core';
import { ProjectStageAPIService } from './project-stage-api.service';
import { ProjectAPIService } from './project-api.service';
import { catchError, forkJoin, Observable, of, tap, throwError } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ProjectSnapshotService {
  private readonly projectStageService = inject(ProjectStageAPIService);
  private readonly projectApiService = inject(ProjectAPIService);

  projectDateRangesSnapshot = signal(null);
  projectPauseRangesSnapshot = signal(null);
  projectStagesSnapshot = signal(null);

  saveProjectFromSnapshots(projectId: number): Observable<any> {
    const requests = [];

    const dateRanges = this.projectDateRangesSnapshot();
    const pauseRanges = this.projectPauseRangesSnapshot();
    const stages = this.projectStagesSnapshot();

    if (this.hasData(dateRanges)) {
      requests.push({
        type: 'dateRanges',
        request: this.projectStageService.setProjectStage({
          payload: dateRanges,
          ignoreToast: true
        })
      });
    }

    if (this.hasData(pauseRanges)) {
      requests.push({
        type: 'pauseRanges',
        request: this.projectApiService.setProjectPauseRange({
          id: projectId,
          payload: pauseRanges,
          ignoreToast: true
        })
      });
    }

    if (this.hasData(stages)) {
      requests.push({
        type: 'stages',
        request: this.projectApiService.setProjectStage({
          id: projectId,
          payload: { items: stages },
          ignoreToast: true
        })
      });
    }

    if (!requests.length) return of(null);

    return forkJoin(
      requests.map((r) =>
        r.request.pipe(
          catchError((error) =>
            throwError(() => ({
              error,
              type: r.type
            }))
          )
        )
      )
    ).pipe(tap(() => this.resetProjectPayloadsSnapshots()));
  }

  resetProjectPayloadsSnapshots(): void {
    this.projectDateRangesSnapshot.set(null);
    this.projectPauseRangesSnapshot.set(null);
    this.projectStagesSnapshot.set(null);
  }

  private hasData(data: any): boolean {
    if (!data) return false;
    if (Array.isArray(data)) return data.length > 0;
    if (typeof data === 'object') return Object.keys(data).length > 0;
    return true;
  }
}
